import { useEffect, useMemo, useRef, useState } from 'react';
import { Boxes, Plus, Trash2, PackageCheck, ScanLine, Printer, FileText, X, AlertTriangle, Check } from '@/components/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/form/Input';
import { ItemThumb } from '@/components/ui/ItemThumb';
import { useToast } from '@/components/ui/useToast';
import { usePrompt } from '@/components/ui/usePrompt';
import { useDocViewer } from '@/components/ui/useDocViewer';
import {
  addBoxItem,
  getItemBoxDefaults,
  createBox,
  getPacking,
  listBoxes,
  removeBox,
  removeBoxItem,
  rejectPacking,
  setBoxWeight,
  supervisorConfirmPacking,
  managerConfirmPacking,
  previewSerial,
  type BuilderBox,
} from '@/api/packing';
import { apiError } from '@/lib/apiError';

interface Props {
  open: boolean;
  packingId: number | null;
  onClose: () => void;
}

const n = (v: number | null | undefined, d = 3) =>
  v == null ? '—' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: d });

export function AssortedPackingModal({ open, packingId, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const ask = usePrompt();
  const docViewer = useDocViewer();
  const packQuery = useQuery({
    queryKey: ['packing', packingId],
    queryFn: () => getPacking(packingId as number),
    enabled: open && packingId != null,
  });
  const boxesQuery = useQuery({
    queryKey: ['packing-boxes', packingId],
    queryFn: () => listBoxes(packingId as number),
    enabled: open && packingId != null,
  });
  const pack = packQuery.data;
  const boxes = useMemo(() => boxesQuery.data ?? [], [boxesQuery.data]);
  const readOnly = pack?.status === 'GRN-Out' || pack?.rejected;

  function refresh() {
    qc.invalidateQueries({ queryKey: ['packing', packingId] });
    qc.invalidateQueries({ queryKey: ['packing-boxes', packingId] });
  }

  // Packed-per-item is derived from the box lines (server keeps packedQty synced).
  const packedByCode = useMemo(() => {
    const m = new Map<string, number>();
    for (const b of boxes) {
      for (const it of b.items) {
        if (!it.itemCode) continue;
        m.set(it.itemCode, (m.get(it.itemCode) ?? 0) + it.qty);
      }
    }
    return m;
  }, [boxes]);

  const targets = useMemo(
    () =>
      (pack?.items ?? []).map((it) => {
        const target = Number(it.pickedQty);
        const packed = packedByCode.get(it.itemCode ?? '') ?? 0;
        return { ...it, target, packed, remaining: Math.max(0, target - packed) };
      }),
    [pack, packedByCode],
  );
  const allPacked = targets.length > 0 && targets.every((t) => t.packed >= t.target - 1e-6);

  const createBoxMut = useMutation({
    mutationFn: () => createBox(packingId as number),
    onSuccess: refresh,
    onError: (e) => toast.push(apiError(e, 'Could not add box'), 'error'),
  });

  const confirmMut = useMutation({
    mutationFn: (supervisor: string) => supervisorConfirmPacking(packingId as number, supervisor),
    onSuccess: () => {
      toast.push('Packing submitted for verification', 'success');
      refresh();
      qc.invalidateQueries({ queryKey: ['packing'] });
      onClose();
    },
    onError: (e) => toast.push(apiError(e, 'Confirm failed'), 'error'),
  });

  const managerMut = useMutation({
    mutationFn: () => managerConfirmPacking(packingId as number),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['packing'] });
      const auto = res.autoDispatch;
      if (auto && 'grnNo' in auto) {
        // Online order — the dispatch note was raised automatically.
        qc.invalidateQueries({ queryKey: ['outward-grn'] });
        qc.invalidateQueries({ queryKey: ['shipping-rows'] });
        if (auto.status === 'Weight-Hold') {
          toast.push(`Packing verified — ${auto.grnNo} is on weight hold. Override it under Dispatch Note → Weight holds to send it to Shipping.`, 'warning');
        } else {
          toast.pushStep('Packing verified — sent to Shipping', {
            body: `Online order dispatched as ${auto.grnNo}. Next step — book the courier.`,
            href: '/sales/shipping',
            nextLabel: 'Open Shipping',
            tag: `packing-manager-${packingId}`,
          });
        }
      } else if (auto && 'error' in auto) {
        toast.push(`Packing verified, but it couldn't be sent to Shipping automatically: ${auto.error} — raise the Dispatch Note manually.`, 'error');
      } else {
        toast.pushStep('Manager verified packing', {
          body: 'Next step — raise the Outward GRN (Dispatch Note).',
          href: '/sales/outward-grn',
          nextLabel: 'Open Dispatch Note',
          tag: `packing-manager-${packingId}`,
        });
      }
      onClose();
    },
    onError: (e) => toast.push(apiError(e, 'Confirm failed'), 'error'),
  });

  const rejectMut = useMutation({
    mutationFn: (reason: string) => rejectPacking(packingId as number, reason),
    onSuccess: () => {
      toast.push('Packing rejected — pick list reopened', 'info');
      refresh();
      qc.invalidateQueries({ queryKey: ['packing'] });
      onClose();
    },
    onError: (e) => toast.push(apiError(e, 'Reject failed'), 'error'),
  });

  async function onConfirm() {
    const sup = await ask({ title: `Submit ${pack?.packNo ?? 'packing'}`, label: 'Supervisor name', confirmLabel: 'Submit' });
    if (!sup?.trim()) return;
    confirmMut.mutate(sup.trim());
  }
  async function onReject() {
    const reason = await ask({ title: `Reject ${pack?.packNo ?? 'packing'}?`, message: 'The pick list reopens and packed stock returns to the warehouse.', label: 'Reason', multiline: true, tone: 'danger', confirmLabel: 'Reject packing' });
    if (!reason?.trim()) return;
    rejectMut.mutate(reason.trim());
  }

  return (
    <Modal
      open={open}
      title={pack ? `Assorted Packing — ${pack.onlineOrder?.orderName ?? pack.packNo}` : 'Packing'}
      onClose={onClose}
      size="xl"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>
            Close
          </Button>
          {!readOnly && (
            <>
              <Button variant="danger" onClick={onReject} disabled={rejectMut.isPending}>
                Reject
              </Button>
              {!pack?.supervisorConfirmed ? (
                <Button
                  variant="primary"
                  onClick={onConfirm}
                  disabled={confirmMut.isPending || !allPacked}
                  title={allPacked ? 'Submit for supervisor verification' : 'Pack every item to its target first'}
                >
                  <span className="inline-flex items-center gap-1">
                    <PackageCheck size={14} /> Submit
                  </span>
                </Button>
              ) : !pack?.managerConfirmed ? (
                <Button
                  variant="success"
                  onClick={() => managerMut.mutate()}
                  disabled={managerMut.isPending}
                  title="Manager verify — moves the packing to Verified so it can be dispatched (Outward GRN)"
                >
                  <span className="inline-flex items-center gap-1">
                    <PackageCheck size={14} /> Manager Confirm
                  </span>
                </Button>
              ) : (
                <Button variant="success" disabled title="Verified — ready to dispatch via Outward GRN">
                  <span className="inline-flex items-center gap-1">
                    <PackageCheck size={14} /> Verified
                  </span>
                </Button>
              )}
            </>
          )}
        </>
      }
    >
      {!pack ? (
        <div className="py-8 text-center text-sm text-[var(--text-muted)]">Loading…</div>
      ) : (
        <div className="flex flex-col gap-3">
          {/* Header */}
          <div className="grid grid-cols-2 gap-2 rounded-md border border-[var(--border-color)] bg-[var(--bg-soft)] p-2.5 text-xs sm:grid-cols-4">
            <Meta label={pack.onlineOrder ? 'Order ID' : 'DO'}>{(pack.onlineOrder ? pack.onlineOrder.orderName : pack.pickList?.dispatchOrder?.doNo) ?? '—'}</Meta>
            <Meta label="Customer">{pack.onlineOrder?.customerName ?? pack.pickList?.dispatchOrder?.party?.name ?? '—'}</Meta>
            <Meta label="Status">{pack.status}</Meta>
            <Meta label="Boxes">{boxes.length}</Meta>
          </div>

          {(pack.pickList?.dispatchOrder?.enquiry?.remarks || pack.pickList?.dispatchOrder?.remarks) && (
            <div className="space-y-1 rounded-md border border-info/30 bg-info/[0.06] px-2.5 py-1.5 text-[11px] leading-snug sm:py-2 sm:text-xs">
              {pack.pickList?.dispatchOrder?.enquiry?.remarks && (
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Enquiry Note</span>{' '}
                  <span className="text-[var(--text-secondary)]">{pack.pickList.dispatchOrder.enquiry.remarks}</span>
                </div>
              )}
              {pack.pickList?.dispatchOrder?.remarks && (
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">DO Remark</span>{' '}
                  <span className="text-[var(--text-secondary)]">{pack.pickList.dispatchOrder.remarks}</span>
                </div>
              )}
            </div>
          )}

          {/* Item targets / progress */}
          <div className="rounded-md border border-[var(--border-color)] p-2.5">
            <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
              Items to pack
            </div>
            <div className="flex flex-col gap-1.5">
              {targets.map((t) => (
                <div key={t.id} className="flex items-center gap-3 text-xs">
                  {/* Large enough for the packer to recognise the piece; click to view full size. */}
                  <ItemThumb src={t.imageUrl} size={64} />
                  {/* Barcode sits beside the name, bold, so the packer can match it at a glance. */}
                  <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-4 gap-y-0.5">
                    <span className="min-w-0 truncate text-[13px] font-semibold">{t.itemName}</span>
                    {t.barcode && <span className="whitespace-nowrap font-mono text-[13px] font-extrabold text-[var(--text-primary)]">({t.barcode})</span>}
                  </span>
                  {/* Phone: qty over the "left" badge so the item name keeps its width. */}
                  <span className="flex shrink-0 flex-col items-end gap-0.5 sm:flex-row sm:items-center sm:gap-2">
                    <span
                      className={
                        'whitespace-nowrap ' +
                        (t.packed >= t.target - 1e-6
                          ? 'font-bold text-success'
                          : 'font-bold text-[var(--text-secondary)]')
                      }
                    >
                      {n(t.packed, 0)} / {n(t.target, 0)} {t.unit}
                    </span>
                    {t.remaining > 1e-6 && (
                      <span className="whitespace-nowrap rounded-full bg-warning/15 px-1.5 text-[10px] font-bold text-warning">
                        {n(t.remaining, 0)} left
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* New box — tare is captured per box below */}
          {!readOnly && (
            <div className="flex items-center justify-between gap-2 rounded-md border border-dashed border-[var(--border-color)] p-2.5">
              <span className="text-[10px] leading-snug text-[var(--text-muted)] sm:text-[11px]">
                Open a new carton, then scan items into it and record its weight.
              </span>
              <Button variant="info" className="shrink-0 whitespace-nowrap" onClick={() => createBoxMut.mutate()} disabled={createBoxMut.isPending}>
                <span className="inline-flex items-center gap-1">
                  <Plus size={14} /> New Box
                </span>
              </Button>
            </div>
          )}

          {/* Boxes */}
          {boxes.length === 0 ? (
            <div className="flex flex-col items-center gap-1 py-6 text-center text-sm text-[var(--text-muted)]">
              <Boxes size={28} className="opacity-50" />
              Add a box, then scan items into it with a qty.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {boxes.map((box) => (
                <BoxCard
                  key={box.id}
                  box={box}
                  packingId={pack.id}
                  readOnly={!!readOnly}
                  onChanged={refresh}
                  onError={(m) => toast.push(m, 'error')}
                  onPrintLabel={() =>
                    docViewer.open('packing-box-label', box.id, `${pack.packNo} · Box ${box.boxNo}`)
                  }
                />
              ))}
            </div>
          )}

          {boxes.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="glass"
                size="sm"
                onClick={() => docViewer.open('packing-box-labels', pack.id, pack.packNo)}
              >
                <span className="inline-flex items-center gap-1">
                  <Printer size={13} /> All Box Labels
                </span>
              </Button>
              <Button
                variant="glass"
                size="sm"
                onClick={() => docViewer.open('packing-scan-receipt', pack.id, pack.onlineOrder?.orderName ?? pack.packNo)}
              >
                <span className="inline-flex items-center gap-1">
                  <FileText size={13} /> Packing Slip
                </span>
              </Button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function BoxCard({
  box,
  packingId,
  readOnly,
  onChanged,
  onError,
  onPrintLabel,
}: {
  box: BuilderBox;
  packingId: number;
  readOnly: boolean;
  onChanged: () => void;
  onError: (msg: string) => void;
  onPrintLabel: () => void;
}) {
  const [serial, setSerial] = useState('');
  const [qty, setQty] = useState('1');
  const [tare, setTare] = useState(box.tareWeightKg != null ? String(box.tareWeightKg) : '');
  const [gross, setGross] = useState(box.grossWeightKg != null ? String(box.grossWeightKg) : '');
  const [len, setLen] = useState(box.lengthCm != null ? String(box.lengthCm) : '');
  const [wid, setWid] = useState(box.widthCm != null ? String(box.widthCm) : '');
  const [hgt, setHgt] = useState(box.heightCm != null ? String(box.heightCm) : '');
  const qtyRef = useRef<HTMLInputElement>(null);
  const serialRef = useRef<HTMLInputElement>(null);
  // Qty is at least 1 and steps by whole units.
  const stepQty = (d: number) => setQty((q) => String(Math.max(1, (Number(q) || 1) + d)));

  // Scan preview — debounce the barcode, then resolve it to its item + availability.
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebounced(serial.trim()), 250);
    return () => clearTimeout(id);
  }, [serial]);
  const previewQuery = useQuery({
    queryKey: ['serial-preview', packingId, debounced],
    queryFn: () => previewSerial(packingId, debounced),
    enabled: debounced.length > 0 && !readOnly,
    staleTime: 15_000,
  });
  const preview = debounced.length > 0 ? previewQuery.data : undefined;

  // Carton details carried over from the last box that held this item (shown until saved).
  const [prefilledFrom, setPrefilledFrom] = useState<string | null>(null);
  async function prefillFromLastBox(itemCode: string) {
    const saved = box.tareWeightKg != null || box.grossWeightKg != null || box.lengthCm != null || box.widthCm != null || box.heightCm != null;
    if (saved || tare || gross || len || wid || hgt) return; // never overwrite what's there
    try {
      const d = await getItemBoxDefaults(itemCode, box.id);
      if (!d) return;
      // Nothing worth carrying over (all blank / zero) — leave the fields alone.
      if (![d.tareWeightKg, d.grossWeightKg, d.lengthCm, d.widthCm, d.heightCm].some((v) => v != null && v > 0)) return;
      const s = (v: number | null) => (v != null ? String(v) : '');
      setTare(s(d.tareWeightKg)); setGross(s(d.grossWeightKg));
      setLen(s(d.lengthCm)); setWid(s(d.widthCm)); setHgt(s(d.heightCm));
      setPrefilledFrom(`${itemCode} — last box in ${d.packNo}${d.singleItem ? '' : ' (mixed box)'}, ${d.qty} pc${d.qty === 1 ? '' : 's'}`);
    } catch { /* no prefill — fields stay empty */ }
  }

  const addMut = useMutation({
    mutationFn: () => addBoxItem(box.id, serial.trim(), Number(qty)),
    onSuccess: (r) => {
      setSerial('');
      setQty('1');
      void prefillFromLastBox(r.itemCode);
      onChanged();
      // Warn (not error) when a source box is now empty.
      if (r.serialRemaining === 0) onError(`Serial fully used`);
      // Keep the barcode field focused so the next item scans straight in.
      serialRef.current?.focus();
    },
    onError: (e) => onError(apiError(e, 'Add failed')),
  });
  const weightMut = useMutation({
    mutationFn: () =>
      setBoxWeight(box.id, gross ? Number(gross) : 0, tare !== '' ? Number(tare) : undefined, {
        lengthCm: len !== '' ? Number(len) : undefined,
        widthCm: wid !== '' ? Number(wid) : undefined,
        heightCm: hgt !== '' ? Number(hgt) : undefined,
      }),
    onSuccess: () => { setPrefilledFrom(null); onChanged(); },
    onError: (e) => onError(apiError(e, 'Weight failed')),
  });
  const rmLineMut = useMutation({
    mutationFn: (id: number) => removeBoxItem(id),
    onSuccess: onChanged,
    onError: (e) => onError(apiError(e, 'Remove failed')),
  });
  const rmBoxMut = useMutation({
    mutationFn: () => removeBox(box.id),
    onSuccess: onChanged,
    onError: (e) => onError(apiError(e, 'Remove failed')),
  });

  function submitAdd() {
    if (!serial.trim() || !qty || Number(qty) <= 0) return;
    addMut.mutate();
  }

  return (
    <div className="rounded-md border border-[var(--border-color)] bg-[var(--card-bg)] p-2.5">
      {/* Phone: Box no + Label/delete on the first row, size & weights on a full-width row under it. */}
      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <div className="shrink-0 whitespace-nowrap text-sm font-black text-accent">Box {box.boxNo}</div>
        <div className="order-last flex w-full flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-[var(--text-secondary)] sm:order-none sm:ml-auto sm:w-auto">
          {(box.lengthCm != null || box.widthCm != null || box.heightCm != null) && (
            <span className="font-semibold">{n(box.lengthCm, 1)}×{n(box.widthCm, 1)}×{n(box.heightCm, 1)} cm</span>
          )}
          <span>Tare {n(box.tareWeightKg)}</span>
          <span className="font-bold">Gross {n(box.grossWeightKg)}</span>
          <span>Net {n(box.netWeightKg)}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="glass"
            size="sm"
            onClick={onPrintLabel}
            disabled={box.items.length === 0}
            title={box.items.length === 0 ? 'Add items before printing a label' : 'Print this box label'}
          >
            <span className="inline-flex items-center gap-1">
              <Printer size={12} /> Label
            </span>
          </Button>
          {!readOnly && (
            <button
              type="button"
              onClick={() => rmBoxMut.mutate()}
              disabled={rmBoxMut.isPending}
              className="rounded p-0.5 text-danger hover:bg-danger/10"
              title="Remove box"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Contents */}
      {box.items.length === 0 ? (
        <div className="py-1 text-[11px] text-[var(--text-muted)]">Empty — scan an item below.</div>
      ) : (
        <ul className="mb-1.5 flex flex-col gap-0.5">
          {box.items.map((it) => (
            <li key={it.id} className="flex items-center justify-between gap-2 text-xs">
              <span className="min-w-0 flex-1 truncate">
                {it.itemName} — <strong>{n(it.qty, 0)}</strong>
                {it.sourceSerialNo && (
                  <span className="ml-1 text-[10px] text-[var(--text-muted)]">({it.sourceSerialNo})</span>
                )}
              </span>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => rmLineMut.mutate(it.id)}
                  className="text-danger hover:opacity-70"
                  title="Remove line"
                >
                  <X size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!readOnly && (
        <div className="flex flex-col gap-2.5">
          {/* Scan row — barcode + qty stepper on one line, with column headings.
              Phone: barcode takes the full row; qty + Add sit underneath. */}
          <div className="flex flex-wrap items-end gap-2 sm:flex-nowrap">
            <div className="w-full min-w-0 sm:w-auto sm:flex-1">
              <Th>Item Barcode</Th>
              <div className="relative">
                <ScanLine
                  size={15}
                  strokeWidth={2.25}
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
                />
                <Input
                  ref={serialRef}
                  value={serial}
                  onChange={(e) => setSerial(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      // Scanner sends the barcode + Enter → add straight away
                      // (qty defaults to 1). Focus returns here on success, so
                      // the next item scans without re-clicking. Use the qty
                      // stepper + Add button when a line needs more than 1.
                      submitAdd();
                    }
                  }}
                  placeholder="Scan item barcode"
                  className="w-full !pl-8"
                />
              </div>
            </div>
            <div className="shrink-0">
              <Th>Qty</Th>
              <div className="flex items-stretch overflow-hidden rounded-md border border-[var(--border-color)] bg-[var(--input-bg)] transition focus-within:border-accent sm:rounded-lg">
                <button
                  type="button"
                  onClick={() => stepQty(-1)}
                  className="flex w-8 items-center justify-center border-r border-[var(--border-color)] text-base font-bold leading-none text-[var(--text-secondary)] transition hover:bg-accent/10 hover:text-accent"
                  aria-label="Decrease qty"
                >
                  −
                </button>
                <input
                  ref={qtyRef}
                  type="number"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      submitAdd();
                    }
                  }}
                  className="w-14 bg-transparent py-1 text-center text-xs font-semibold text-[var(--text-primary)] outline-none [appearance:textfield] sm:py-2 sm:text-sm [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
                <button
                  type="button"
                  onClick={() => stepQty(1)}
                  className="flex w-8 items-center justify-center border-l border-[var(--border-color)] text-base font-bold leading-none text-[var(--text-secondary)] transition hover:bg-accent/10 hover:text-accent"
                  aria-label="Increase qty"
                >
                  +
                </button>
              </div>
            </div>
            <Button variant="primary" size="sm" className="flex-1 sm:flex-none" onClick={submitAdd} disabled={addMut.isPending}>
              Add
            </Button>
          </div>

          {/* Scan preview — what the barcode resolved to */}
          {debounced.length > 0 && (
            <div className="-mt-1 text-[11px] font-semibold">
              {previewQuery.isFetching && !preview ? (
                <span className="text-[var(--text-muted)]">Looking up “{debounced}”…</span>
              ) : !preview?.found ? (
                <span className="text-danger"><X size={13} className="mr-1 inline-block align-[-2px]" /> Barcode not found in warehouse</span>
              ) : preview.dispatched ? (
                <span className="text-danger"><X size={13} className="mr-1 inline-block align-[-2px]" /> {preview.itemName} — already dispatched</span>
              ) : !preview.onPickList ? (
                <span className="text-warning"><AlertTriangle size={13} className="mr-1 inline-block align-[-2px]" /> {preview.itemName} — not on this pick list</span>
              ) : !preview.available && preview.inThisPackingBoxes?.length ? (
                <span className="text-success">
                  <Check size={13} className="mr-1 inline-block align-[-2px]" /> {preview.itemName} — already packed in Box {preview.inThisPackingBoxes.join(', ')} of this packing
                </span>
              ) : !preview.available ? (
                <span className="text-warning">
                  <AlertTriangle size={13} className="mr-1 inline-block align-[-2px]" /> {preview.itemName} — nothing left on this barcode (already packed for another order)
                </span>
              ) : (
                <span className="text-success">
                  <Check size={13} className="mr-1 inline-block align-[-2px]" /> {preview.itemName} · <strong>{preview.available}</strong> available on this
                  barcode
                </span>
              )}
            </div>
          )}

          {/* Box weights + dimensions — net is computed server-side */}
          {/* Phone: a 3-column grid with short labels (5 fields + Save fill two rows). */}
          <div className="grid grid-cols-3 items-end gap-2 border-t border-[var(--border-color)] pt-2.5 sm:flex sm:flex-wrap">
            <div>
              <Th><span className="sm:hidden">Tare kg</span><span className="hidden sm:inline">Empty Box (Tare) kg</span></Th>
              <Input type="number" value={tare} onChange={(e) => setTare(e.target.value)} placeholder="0.000" className="w-full sm:w-24" />
            </div>
            <div>
              <Th><span className="sm:hidden">Gross kg</span><span className="hidden sm:inline">Gross Weight (kg)</span></Th>
              <Input type="number" value={gross} onChange={(e) => setGross(e.target.value)} placeholder="0.000" className="w-full sm:w-24" />
            </div>
            <div>
              <Th><span className="sm:hidden">L cm</span><span className="hidden sm:inline">Length (cm)</span></Th>
              <Input type="number" value={len} onChange={(e) => setLen(e.target.value)} placeholder="0" className="w-full sm:w-20" />
            </div>
            <div>
              <Th><span className="sm:hidden">W cm</span><span className="hidden sm:inline">Width (cm)</span></Th>
              <Input type="number" value={wid} onChange={(e) => setWid(e.target.value)} placeholder="0" className="w-full sm:w-20" />
            </div>
            <div>
              <Th><span className="sm:hidden">H cm</span><span className="hidden sm:inline">Height (cm)</span></Th>
              <Input type="number" value={hgt} onChange={(e) => setHgt(e.target.value)} placeholder="0" className="w-full sm:w-20" />
            </div>
            <Button
              variant="glass"
              size="sm"
              className="w-full justify-center sm:w-auto"
              onClick={() => weightMut.mutate()}
              disabled={weightMut.isPending || (!gross && !tare && !len && !wid && !hgt)}
            >
              Save box
            </Button>
          </div>
          {prefilledFrom && (
            <div className="mt-1.5 text-[11px] font-semibold text-amber-600">
              Filled from {prefilledFrom} — check the weights, then Save box.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-0.5 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
      {children}
    </div>
  );
}

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-[var(--text-muted)]">{label}</div>
      <div className="font-semibold">{children}</div>
    </div>
  );
}
